/// <reference path="Utilities/_reference.ts" />

module YVR.Artwork {
	// Dependencies
	import gtm = YVR.Utilities.Gtm;
	import events = YVR.Utilities.Events;
	import logger = YVR.Utilities.Logger;
	import i18n = YVR.Utilities.I18n;

	// Default options
	const options = {
		artwork: {
			serviceUrl: `/${i18n.getCurrentLanguage()}/_api/Artwork`,
			select: [
				'Description',
				'FacilityId',
				'IsOpenNow',
				'NextOpenOrClosedTime',
				'PoiAccessibility',
				'PoiAccessibilityLongDescription',
				'PoiBuildingLevel',
				'PoiCategory',
				'PoiGate',
				'PoiID',
				'PoiKeywords',
				'PoiLogo',
				'PoiNameLocID',
				'PoiSubCategory',
				'PoiTaglineLocID',
				'PoiTerminal',
				'PoiTerminalLongDescription',
				'PoiYVRLocationDesc',
				'StoreFront_4x3_Url',
				'StoreFront_4x3_2x_Url',
				'SubCategoryDescription'
			]
		},
		types: {
			serviceUrl: `/${i18n.getCurrentLanguage()}/_api/Artwork`,
			select: [
				'PoiCategory',
				'PoiSubCategory',
				'SubCategoryDescription'
			]
		}
	};

	const ANALYTICS_CATEGORY = 'artwork-listing';

	// Cache
	const cache = {
		template: null
	};

	// Current artwork list
	let artworkCache: IFacility[] = [];

	let currentKeywordMaxLength = 1;

	// Cached elements
	const elements = {
		block: $('.yvr-artwork-list'),
		buttons: $('.yvr-facility-list__tab-switcher-button'),
		keyword: $('#yvr-facility-list-search'),
		list: $('.yvr-facility-list__list'),
		opennow: $('#yvr-form-opennow'),
		service: $('.yvr-tab-switcher__button'),
		terminal: $('input[name="terminal-toggle"]'),
		script: $('#yvr-facility-template'),
		security: $('input[name="security-toggle"]'),
		tabs: $('.yvr-facility-list__tab-content')
	};


	/**
	 * Main.
	 */
	function main() {
		if (elements.block.length) {
			eventBindings();
			runArtwork();
		}
	}


	/**
	 * Event bindings.
	 */
	function eventBindings() {
		filterKeywordEventBinding();
		toggleEventBinding();
	}


	/**
	 * Run through the steps to get and render artwork.
	 */
	function runArtwork() {
		getArtwork()
			.then(transformArtworkData)
			.then(renderArtwork)
			.then(() => {
				filterArtworkData(artworkCache);
			})
			.fail(() => {
				logger.error('[artwork][runArtwork] An error occured retrieving the facility data.');

				renderArtwork({ error: true });
			});
	}


	/**
	 * Get the artwork from the web service.
	 * @returns {Object} A jQuery Promise.
	 */
	function getArtwork(): JQueryPromise<any> {
		// Build the OData service URL with our filters
		const url = `${options.artwork.serviceUrl}?$select=${options.artwork.select.join(',')}`;

		if (cache[url]) {
			// If the data is found in the cache
			const deferred = $.Deferred();

			deferred.resolve(cache[url]);

			return deferred.promise();
		} else {
			// Data is not found, get a service call
			return $.ajax({
				url,
				type: 'GET',
				success(data) {
					cache[url] = data;
					logger.log('[artwork][getArtwork] Successfully retrieved the facility data.');
				}
			});
		}
	}


	/**
	 * Get the facility filter values from the form.
	 * @returns {Object} All the filter data.
	 */
	function getFilterFormData() {
		return {
			terminal: elements.terminal.filter(':checked').val(),
			security: elements.security.filter(':checked').val(),
			opennow: elements.opennow.is(':checked')
		};
	}


	/**
	 * Transforms the facility data and removes unnecessary OData metadata.
	 * @param {Object} data The facility data from the OData web service.
	 * @returns {Object}    The trasnformed facility data.
	 */
	function transformArtworkData(data) {
		const list = data.value;

		list.sort((a, b) => a.PoiNameLocID.localeCompare(b.PoiNameLocID));

		return {
			data: list
		};
	}


	/**
	 * Filters the facility data list by hiding and showing elements.
	 */
	function filterArtworkData(list: IFacility[]) {
		const data = getFilterFormData();
		const keyword = elements.keyword.val().removeDiacritics().toLocaleLowerCase();

		// Update the current artwork list by removing elements that do not need to be shown.
		if (keyword) {
			list = $.grep(list, node => (node.PoiNameLocID && node.PoiNameLocID.removeDiacritics().toLocaleLowerCase().includes(keyword)) ||
				(node.Description && node.Description.removeDiacritics().toLocaleLowerCase().includes(keyword)) ||
				(node.PoiTaglineLocID && node.PoiTaglineLocID.removeDiacritics().toLocaleLowerCase().includes(keyword)) ||
				(node.PoiKeywords && node.PoiKeywords.removeDiacritics().toLocaleLowerCase().includes(keyword)));
		}

		if (data.terminal !== 'All') {
			list = $.grep(list, node => node.PoiTerminal === data.terminal);
		}

		if (data.security !== 'All') {
			list = $.grep(list, node => node.PoiAccessibility === data.security);
		}

		if (data.opennow) {
			list = $.grep(list, node => node.IsOpenNow === data.opennow);
		}

		// Convert the list of elements to an array of IDs for easier usage.
		const listIds = jQuery.map(list, (element, index) => element.PoiID);

		// Loop through each facility and match the IDs to the global list.
		elements.block.find('.yvr-facility-list__item').each((index, element) => {
			const facility = $(element);
			const dataId = element.getAttribute('data-id');
			const id = parseInt(dataId, 10);

			if (!listIds.includes(id)) {
				facility.hide();
			} else {
				facility.show();
			}
		});
	}


	/**
	 * Render the facility listing.
	 * @param {Object} data The facility data from the OData web service.
	 */
	function renderArtwork(data) {
		if (!cache.template) {
			if (elements.script.length) {
				cache.template = Handlebars.compile(elements.script.html());
			}
		}

		artworkCache = data.data;

		const html = cache.template(data);

		elements.list.html(html);
	}


	/**
	 * Filter through the facility list with the current keyword.
	 */
	function filterKeywordEventBinding() {
		// Get the new query from the input
		elements.block.on('input keyup', '#yvr-facility-list-search', events.throttle(e => {
			filterArtworkData(artworkCache);

			const value = $(e.target).val().trim();

			if (value && value.length > currentKeywordMaxLength) {
				gtm.eventToDataLayer(ANALYTICS_CATEGORY, 'filter|keyword', value);

				currentKeywordMaxLength = value.length;
			}
		}, 200));

		elements.block.on('click', '.yvr-form__input-button', e => {
			e.preventDefault();

			filterArtworkData(artworkCache);
		});
	}


	/**
	 * Re-run the get and render functions when terminal or security inputs change.
	 */
	function toggleEventBinding() {
		elements.block.on('change', '.yvr-form__select, .yvr-form__toggle, .yvr-form__checkbox', e => {
			filterArtworkData(artworkCache);
		});

		elements.block.on('change', '.yvr-form__toggle[name="terminal-toggle"]', e => {
			gtm.eventToDataLayer(ANALYTICS_CATEGORY, 'filter|terminal', $(e.target).val().trim());
		});

		elements.block.on('change', '.yvr-form__toggle[name="security-toggle"]', e => {
			gtm.eventToDataLayer(ANALYTICS_CATEGORY, 'filter|accessibility', $(e.target).val().trim());
		});

		elements.block.on('change', '.yvr-form__checkbox', e => {
			const label = $(e.target).is(':checked') ? 'open now' : 'all';

			gtm.eventToDataLayer(ANALYTICS_CATEGORY, 'filter|open-now', label);
		});
	}

	main();
}
